import { useState } from 'react';
import { WaitlistStatus } from '@sportspredict/shared';
import { useSendWaitlistNotifyMutation } from '@/store/api';
import { useToast } from '@/components/ui/use-toast';
import { downloadCSV } from '@/utils';
import { format } from 'date-fns';

const WAITLIST_NOTIFY_ALL_JOB_DELAY_MS = 3000;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function useWaitlistNotifications(event: any, waitlistUsers: any[]) {
	const [isNotifying, setIsNotifying] = useState(false);
	const { toast } = useToast();
	const [sendWaitlistNotify] = useSendWaitlistNotifyMutation();

	const handleExportWaitlist = () => {
		try {
			const headers = ['Name', 'Email', 'Status', 'Date Added'];
			const rows = [
				headers,
				...waitlistUsers.map((user) => [
					user.user_name,
					user.user_email,
					user.status,
					format(new Date(user.created_date), 'yyyy-MM-dd HH:mm:ss'),
				]),
			];

			const safeName = event?.title?.replace(/[^a-z0-9]/gi, '_').toLowerCase();
			const filename = `${safeName}_waitlist_${format(new Date(), 'yyyy-MM-dd')}.csv`;

			downloadCSV(rows, filename);

			toast({
				title: 'Success',
				description: 'Waitlist exported successfully',
				className: 'bg-black border-0',
			} as any);
		} catch (error) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Failed to export waitlist',
				className: 'bg-black border-0',
			} as any);
			console.error(error);
			throw new Error('Error exporting waitlist');
		}
	};

	const notifyUser = async (user: any) => {
		try {
			setIsNotifying(true);

			await sendWaitlistNotify({ eventId: event?.id, waitlistId: user.id }).unwrap();

			toast({
				title: 'Success',
				description: 'Notification sent successfully',
				className: 'bg-black border-0',
			} as any);
		} catch (error) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Failed to send notification',
				className: 'bg-black border-0',
			} as any);
			console.error(error);
			throw new Error('Error sending notification');
		} finally {
			setIsNotifying(false);
		}
	};

	const notifyAll = async () => {
		try {
			setIsNotifying(true);

			const pendingUsers = waitlistUsers.filter((user) => user.status === WaitlistStatus.Pending);

			if (pendingUsers.length === 0) {
				toast({
					title: 'Info',
					description: 'No pending users to notify',
					className: 'bg-black border-0',
				} as any);
				return;
			}

			await sendWaitlistNotify({ eventId: event?.id }).unwrap();

			await delay(WAITLIST_NOTIFY_ALL_JOB_DELAY_MS);

			toast({
				title: 'Success',
				description: 'Notifications are being sent to pending users',
				className: 'bg-black border-0',
			} as any);
		} catch (error: any) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description:
					error?.status === 409
						? 'There are no pending users to notify for this event'
						: 'Failed to send notifications',
				className: 'bg-black border-0',
			} as any);
			console.error(error);
			throw new Error('Error sending notifications');
		} finally {
			setIsNotifying(false);
		}
	};

	return {
		isNotifying,
		handleExportWaitlist,
		notifyUser,
		notifyAll,
	};
}
